import * as Three from 'three';
import {UNIT_COEF} from "../constants";
import {getVertexRenderCoords, get2dCoord} from "../helper";
import {assignUVs, applyTexture, createShape} from '../three-helper';

class Wall3D {
    name = '';
    prototype = 'lines';
    properties = {
        height: {
            label: 'Height',
            type: 'length-measure',
            defaultValue: {
                length: 3000,
            }
        },
        visible: {
            label: 'Visible',
            type: 'checkbox',
            defaultValue: true
        },
        thickness: {
            label: 'Thickness',
            type: 'hidden',
            defaultValue: {
                length: 0.1
            }
        },
        faceA: {
            type: 'hidden',
            defaultValue: {
                opacity: 0,
                color: '#ffffff'
            }
        },
        faceB: {
            type: 'hidden',
            defaultValue: {
                opacity: 0,
                color: '#aaaaaa'
            }
        }
    };

    constructor(props) {

    }

    static createCustomTexture(uri, name) {
        return {uri, name}
    }

    _splitTexture(texture, coords, wallMaterial, element, area, distance, height, inverted) {
        const lineID = element.id,
            ceiling = area.properties.scene.ceiling >>> 0;
        const {posePitch, poseRoll} = area.properties.scene;

        return texture.splitTexture(coords, lineID, ceiling, 'wall', inverted, {posePitch, poseRoll})
            .then((data) => {
                let uri = data.src;
                let wallTexture = Wall3D.createCustomTexture(uri, 'wall');
                this._sendPostMsg(area.name, wallTexture.uri, 'wall')

                return applyTexture(wallMaterial, wallTexture).then(() => data)
            })
    }

    _sendPostMsg(name, uri, type) {
        window.postMessage({
            event: 'loadTexture',
            name,
            type,
            url: uri
        }, '*');
    }

    _loadTexture(element, wallMaterial) {
        let wallTexture = Wall3D.createCustomTexture(element.texture, 'wall');
        return applyTexture(wallMaterial, wallTexture)
    }

    render3D(element, layer, scene, texture) {
        const sceneUnit = scene.unit || 'mm';
        const coef = UNIT_COEF[sceneUnit];

        let vertex0 = getVertexRenderCoords(layer.vertices[element.vertices[0]], sceneUnit);
        let vertex1 = getVertexRenderCoords(layer.vertices[element.vertices[1]], sceneUnit);

        let areaId = layer.vertices[element.vertices[0]].areas[0];
        let area = layer.areas[areaId];
        let inverted = false;

        const [start, end] = get2dCoord(layer.vertices, element);

        this._correctSameCoord(vertex0, vertex1);

        if (vertex0.x > vertex1.x ) {
            let app = vertex0;
            vertex0 = vertex1;
            vertex1 = app;
            inverted = true;
        }

        // Get height and thickness of the wall converting them into the current scene units
        let height = element.properties.height.length * coef;
        let distance = Math.sqrt(Math.pow(vertex0.x - vertex1.x, 2) + Math.pow(vertex0.y - vertex1.y, 2));
        let alpha = Math.asin((vertex1.y - vertex0.y) / (distance));
        let wallCoords = [
            [0, 0],
            [distance, 0],
            [distance, height],
            [0, height]
        ];
        let rectShape = createShape(wallCoords);
        let wallGeometry = new Three.ShapeGeometry(rectShape);

        wallGeometry.computeVertexNormals();

        let wallMaterial = new Three.MeshPhongMaterial({
            side: Three.DoubleSide,
            opacity: 1,
            transparent: true,
            alphaTest : 0.5,
        });

        // Assign the correct UV coordinates
        wallGeometry = assignUVs(wallGeometry);

        let wall = new Three.Mesh(wallGeometry, wallMaterial);

        // Rotate the wall around the bottom-left vertex
        wall.rotation.y = alpha;

        return !texture
            ? this._loadTexture(element, wallMaterial, distance, height)
                .then(() => ({obj:wall}))
            : this._splitTexture(texture, [start, end], wallMaterial, element, area, distance, height, inverted)
                .then((data) => ({obj:wall, data}))
    }

    _correctSameCoord(vertex0, vertex1){
        if (vertex0.x == vertex1.x) {
            vertex0.x -= 0.0000001;
        }
        if (vertex0.y == vertex1.y) {
            vertex0.y -= 0.0000001;
        }
    }

    updateRender3D(element, layer, scene, mesh, oldElement, differences, selfDestroy, selfBuild) {
        let noPerf = () => { selfDestroy(); return selfBuild(); };

        let soul = mesh.getObjectByName('soul');
        let frontFace = mesh.getObjectByName('frontFace');
        let backFace = mesh.getObjectByName('backFace');
        let halfPI = Math.PI / 2;

        if (differences[0] === 'properties' && differences[1] === 'thickness') {
            let newThickness = element.properties.thickness.length;
            let oldThickness = oldElement.properties.thickness.length;
            let halfNewThickness = newThickness / 2;
            let texturedFaceDistance = halfNewThickness + 1;
            let originalThickness = oldThickness / soul.scale.z;
            let alpha = soul.rotation.y;

            let xTemp = texturedFaceDistance * Math.cos(alpha - ( halfPI ) );
            let zTemp = texturedFaceDistance * Math.sin(alpha - ( halfPI ) );

            soul.scale.set( 1, 1, ( newThickness / originalThickness ) );

            frontFace.position.x = soul.position.x + ( xTemp );
            frontFace.position.z = soul.position.z + ( zTemp );

            backFace.position.x = soul.position.x - ( xTemp );
            backFace.position.z = soul.position.z - ( zTemp );
        } else return noPerf();

        return Promise.resolve(mesh);
    }
}

export default Wall3D;
